/**
 * Extract a carrier-facing message from raw TrackHub / HTTP error payloads.
 * Mirrors backend extractCarrierErrorMessage in trackHubShipmentService.
 */
export function extractCarrierErrorMessage(raw) {
  if (raw == null || raw === "") return "";
  let data = raw;
  if (typeof raw === "string") {
    const jsonStart = raw.indexOf("{");
    if (jsonStart >= 0) {
      try {
        data = JSON.parse(raw.slice(jsonStart));
      } catch (_) {
        return raw;
      }
    } else {
      return raw;
    }
  }
  if (typeof data !== "object" || data == null) return String(raw);

  const nested = data.body?.errors || data.errors || data.body?.body?.errors;
  if (Array.isArray(nested) && nested[0]?.message) {
    return String(nested[0].message);
  }
  if (typeof data.error === "string" && data.error.trim()) {
    return String(data.error).trim();
  }
  if (data.body?.message) return String(data.body.message);
  if (data.message && data.message !== "carrier error") return String(data.message);
  if (typeof data.body === "string") return data.body;
  return "";
}

/** Infer trackHubErrors.* code from a carrier / raw error string. */
export function inferTrackHubErrorCode(rawOrMessage) {
  const text = String(rawOrMessage || "");
  if (/not allowed to post order in this country/i.test(text)) {
    return "CARRIER_COUNTRY_NOT_ALLOWED";
  }
  if (/shop price configurations are not set/i.test(text)) {
    return "CARRIER_PRICE_NOT_CONFIGURED";
  }
  if (/distance.*exceeds|maximum allowed distance|DISTANCE_EXCEEDS_CARRIER_LIMIT/i.test(text)) {
    return "DISTANCE_EXCEEDS_CARRIER_LIMIT";
  }
  if (
    /weight.*exceed|maximum.*weight|package weight exceeds|WEIGHT_EXCEEDS_CARRIER_LIMIT/i.test(text)
    || text === "weight_exceeds_carrier_limit"
  ) {
    return "WEIGHT_EXCEEDS_CARRIER_LIMIT";
  }
  if (/no_active_carrier|NO_ACTIVE_CARRIER/i.test(text)) {
    return "NO_ACTIVE_CARRIER";
  }
  if (/carrier is not currently active|CARRIER_INACTIVE|carrier_inactive/i.test(text)) {
    return "CARRIER_INACTIVE";
  }
  if (/geo_unavailable|delivery location|national.?address/i.test(text)) {
    return "GEO_UNAVAILABLE";
  }
  if (/branch_missing_geo/i.test(text)) return "BRANCH_MISSING_GEO";
  if (/branch_not_found/i.test(text)) return "BRANCH_NOT_FOUND";
  if (/not_delivery/i.test(text)) return "NOT_DELIVERY";
  if (/trackhub_not_configured/i.test(text)) return "TRACKHUB_NOT_CONFIGURED";
  if (/timed out|timeout/i.test(text)) return "TIMEOUT";
  if (/manual_shipment_disabled|MANUAL_SHIPMENT_DISABLED/i.test(text)) {
    return "MANUAL_SHIPMENT_DISABLED";
  }
  if (/invalid_carrier|INVALID_CARRIER/i.test(text)) {
    return "INVALID_CARRIER";
  }
  if (/phone number is invalid|invalid.*phone|buyer.*phone|invalid_phone/i.test(text)) {
    return "INVALID_PHONE";
  }
  return null;
}

/**
 * Human-readable TrackHub shipment error with Arabic support.
 * @param {object|null|undefined} trackhub - order.trackhub
 * @param {{ t: Function, isRTL?: boolean, language?: string }} opts
 */
export function formatTrackHubError(trackhub, { t, isRTL, language } = {}) {
  if (!trackhub) return "";

  const preferAr =
    isRTL === true ||
    String(language || "").toLowerCase().startsWith("ar");

  const fromMessage = preferAr
    ? trackhub.lastErrorMessage?.ar || trackhub.lastErrorMessage?.en
    : trackhub.lastErrorMessage?.en || trackhub.lastErrorMessage?.ar;
  if (fromMessage && String(fromMessage).trim()) {
    return String(fromMessage).trim();
  }

  const code =
    trackhub.lastErrorCode ||
    inferTrackHubErrorCode(trackhub.lastError) ||
    inferTrackHubErrorCode(
      extractCarrierErrorMessage(trackhub.lastError)
    );

  if (code && typeof t === "function") {
    const translated = t(`trackHubErrors.${code}`, { defaultValue: "" });
    if (translated && translated !== `trackHubErrors.${code}`) {
      return translated;
    }
  }

  const carrier = extractCarrierErrorMessage(trackhub.lastError);
  if (carrier && !/^HTTP\s+\d+/i.test(carrier)) {
    const inferred = inferTrackHubErrorCode(carrier);
    if (inferred && typeof t === "function") {
      const translated = t(`trackHubErrors.${inferred}`, {
        defaultValue: "",
      });
      if (translated && translated !== `trackHubErrors.${inferred}`) {
        return translated;
      }
    }
    return carrier;
  }

  if (typeof t === "function") {
    return t("trackHubErrors.CREATE_FAILED", {
      defaultValue: t("shipmentFailed", "Shipment failed"),
    });
  }
  return "Shipment failed";
}

const DEFAULT_MAX_DISTANCE_KM = 30;
const DEFAULT_MAX_WEIGHT_KG = 35;

/**
 * Carrier row / banner message for distance or weight limits (quote & create).
 * @param {object} data - API error body (success: false)
 * @param {Function} t
 * @param {{ maxDistanceKm?: number, maxWeightKg?: number }} [opts]
 * @returns {string|null}
 */
export function quoteCarrierRestrictionMessage(data, t, opts = {}) {
  if (!data || typeof t !== "function") return null;
  if (
    data.reason === "distance_exceeds_carrier_limit" ||
    data.error_code === "DISTANCE_EXCEEDS_CARRIER_LIMIT"
  ) {
    const distance = Number(data.distanceKm);
    return t("shipmentsPage.createManual.distanceError", {
      distance: Number.isFinite(distance) ? distance.toFixed(1) : "—",
      max: data.maxDistanceKm || opts.maxDistanceKm || DEFAULT_MAX_DISTANCE_KM,
    });
  }
  if (
    data.reason === "weight_exceeds_carrier_limit" ||
    data.error_code === "WEIGHT_EXCEEDS_CARRIER_LIMIT"
  ) {
    return t("shipmentsPage.createManual.weightExceedsCarrier", {
      max: data.maxWeightKg || opts.maxWeightKg || DEFAULT_MAX_WEIGHT_KG,
    });
  }
  if (
    data.reason === "manual_shipment_disabled" ||
    data.error_code === "MANUAL_SHIPMENT_DISABLED"
  ) {
    return t("trackHubErrors.MANUAL_SHIPMENT_DISABLED");
  }
  return null;
}

/**
 * Format axios / TrackHub API error responses for toasts and banners.
 * Prefers bilingual trackHubErrors.* codes; falls back to message / message_ar by locale.
 * @param {object} error
 * @param {Function} t
 * @param {{ preferAr?: boolean, language?: string }} [opts]
 */
export function formatTrackHubApiError(error, t, opts = {}) {
  const preferAr =
    opts.preferAr === true ||
    String(opts.language || "").toLowerCase().startsWith("ar");

  const data = error?.response?.data;
  if (data) {
    const carrierFromLast = extractCarrierErrorMessage(data.lastError);
    const inferred =
      inferTrackHubErrorCode(carrierFromLast) ||
      inferTrackHubErrorCode(data.message) ||
      inferTrackHubErrorCode(data.reason) ||
      inferTrackHubErrorCode(data.lastError) ||
      inferTrackHubErrorCode(data.error_code) ||
      inferTrackHubErrorCode(data.error);

    // Prefer a specific inferred code over a generic CREATE_FAILED from the API.
    let code = data.error_code || null;
    if (inferred && inferred !== "CREATE_FAILED") {
      code = inferred;
    } else if (!code || code === "CREATE_FAILED") {
      code = inferred || code;
    }

    if (code && typeof t === "function") {
      const translated = t(`trackHubErrors.${code}`, { defaultValue: "" });
      if (translated && translated !== `trackHubErrors.${code}`) {
        return translated;
      }
    }

    if (preferAr && data.message_ar && String(data.message_ar).trim()) {
      return String(data.message_ar).trim();
    }
    if (data.message && String(data.message).trim()) {
      return String(data.message).trim();
    }
    if (data.message_ar && String(data.message_ar).trim()) {
      return String(data.message_ar).trim();
    }
    if (carrierFromLast && !/^HTTP\s+\d+/i.test(carrierFromLast)) {
      return carrierFromLast;
    }
  }
  if (error?.message) return String(error.message);
  return typeof t === "function"
    ? t("trackHubErrors.CREATE_FAILED", { defaultValue: "Shipment failed" })
    : "Shipment failed";
}
